import { makeAutoObservable } from 'mobx'

import { getStoreCreator } from '@/utils/mobx/getStoreCreator'

/**
 * When the phone's pinned «Get first lead» bar is up.
 *
 * The landing is ~11 screens on a phone and had its ask twice: in the hero, at the top, and in the
 * closing block, at the bottom — about nine screens with no button in between, and the nav scrolls
 * away with the first one (mobile audit 2026-09-28, f1211). The bar fills exactly that gap:
 *
 *   - up once the hero's own buttons have scrolled ABOVE the screen. Not while they are on it: two
 *     identical buttons on one screen read as a glitch, and the one in the hero is the one designed
 *     for there;
 *   - down again from the moment the closing block enters the screen, and it stays down below it,
 *     over the footer: the closing block is the same ask, and the bar would lie on the footer's links.
 */
export class LandingStickyCtaStore {
  heroPassed = false
  closingReached = false

  constructor() {
    makeAutoObservable(this)
  }

  get visible() {
    return this.heroPassed && !this.closingReached
  }

  setHeroPassed = (passed: boolean) => {
    this.heroPassed = passed
  }

  setClosingReached = (reached: boolean) => {
    this.closingReached = reached
  }

  /**
   * Watches the two edges while the bar is mounted. Returns the disposer.
   *
   * "Above the screen" and "reached" are read off the entry's rectangle, not only `isIntersecting`: a
   * block that is not on screen is either still ahead or already behind, and the two mean opposite
   * things here. An observer reports its first state as soon as it starts, so a reload in the middle
   * of the page settles the bar without a scroll.
   *
   * No IntersectionObserver, no bar: the page has its two buttons either way, and a bar that cannot
   * know where it is would sit on the closing block.
   */
  watch = (hero: Element, closing: Element): (() => void) => {
    if (typeof IntersectionObserver === 'undefined') return () => undefined
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const { top, bottom } = entry.boundingClientRect
        if (entry.target === hero) this.setHeroPassed(!entry.isIntersecting && bottom <= 0)
        if (entry.target === closing) this.setClosingReached(entry.isIntersecting || top < 0)
      }
    })
    observer.observe(hero)
    observer.observe(closing)
    return () => observer.disconnect()
  }
}

export const useLandingStickyCtaStore = getStoreCreator(LandingStickyCtaStore)
